/**
 * [Phase 2b-D §6.5.1 R6] Keeper Management Panel for DraftLobby.
 *
 * Renders only when:
 *   - Commissioner is viewing
 *   - league.status in (SETUP, LOBBY)
 *   - league.settings.use_keepers === true
 *   - All seats filled (humans + CPUs === team_count) - handled by parent
 *
 * Wires up to 2b-A endpoints:
 *   POST   /api/leagues/{id}/keepers
 *   GET    /api/leagues/{id}/keepers
 *   DELETE /api/leagues/{id}/keepers/{keeper_id}
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Search, Lock as LockIcon } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KeeperManagementPanel({ league, managers }) {
  const [keepers, setKeepers] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(managers?.[0]?.id || "");
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [selectedPlayerLabel, setSelectedPlayerLabel] = useState("");
  const [keeperValue, setKeeperValue] = useState(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/leagues/${league.id}/keepers`,
        { headers: authHeaders() },
      );
      setKeepers(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load keepers");
    }
  }, [league.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Debounced player search.
  useEffect(() => {
    if (!searchQ || searchQ.length < 2) {
      setSearchResults([]);
      return;
    }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API}/players?search=${encodeURIComponent(searchQ)}`,
          { headers: authHeaders() },
        );
        if (active) setSearchResults((data || []).slice(0, 8));
      } catch {
        if (active) setSearchResults([]);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [searchQ]);

  const onAdd = async () => {
    setError("");
    if (!selectedTeamId || !selectedPlayerId || !keeperValue) {
      setError("Pick a team, a player, and a value.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/leagues/${league.id}/keepers`,
        {
          team_id: selectedTeamId,
          player_id: selectedPlayerId,
          keeper_value: Number(keeperValue),
        },
        { headers: authHeaders() },
      );
      setSearchQ("");
      setSearchResults([]);
      setSelectedPlayerId("");
      setSelectedPlayerLabel("");
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not add keeper");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (keeperId) => {
    setError("");
    setBusy(true);
    try {
      await axios.delete(
        `${API}/leagues/${league.id}/keepers/${keeperId}`,
        { headers: authHeaders() },
      );
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not remove keeper");
    } finally {
      setBusy(false);
    }
  };

  // Group keepers by team for display.
  const byTeam = keepers.reduce((acc, k) => {
    (acc[k.team_id] = acc[k.team_id] || []).push(k);
    return acc;
  }, {});

  return (
    <div
      data-testid="lobby-keeper-panel"
      className="glass-card rounded-2xl p-5"
    >
      <h2 className="font-display font-bold text-base uppercase flex items-center gap-2 mb-3 text-[#F5F5F5]">
        <LockIcon size={16} className="text-[#FF6B00]" /> Keepers
      </h2>

      {error && (
        <div className="bg-[#FF4444]/10 border border-[#FF4444]/30 rounded-lg px-3 py-2 text-[#FF4444] font-body text-xs mb-3">
          {error}
        </div>
      )}

      {/* Add keeper form */}
      <div className="space-y-2 mb-4">
        {/* Team selector */}
        <select
          value={selectedTeamId}
          onChange={(e) => setSelectedTeamId(e.target.value)}
          data-testid="lobby-keeper-team-select"
          className="w-full bg-[#1C1C1C] border border-[#252525] rounded-lg px-3 py-2 font-body text-sm text-[#F5F5F5] outline-none"
        >
          <option value="">- Select team -</option>
          {(managers || []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.team_name || `${m.first_name || ""} ${m.last_name || ""}`.trim()}
            </option>
          ))}
        </select>

        {/* Player search */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
            <Search size={14} />
          </span>
          <input
            value={selectedPlayerLabel || searchQ}
            onChange={(e) => {
              setSearchQ(e.target.value);
              setSelectedPlayerId("");
              setSelectedPlayerLabel("");
            }}
            placeholder="Search player by name..."
            data-testid="lobby-keeper-player-search"
            className="w-full pl-9 bg-[#1C1C1C] border border-[#252525] rounded-lg px-3 py-2 font-body text-sm text-[#F5F5F5] outline-none focus:border-[#FF6B00]"
          />
          {searchResults.length > 0 && !selectedPlayerId && (
            <ul
              data-testid="lobby-keeper-search-results"
              className="absolute z-10 left-0 right-0 mt-1 bg-[#1C1C1C] border border-[#252525] rounded-lg shadow-lg max-h-56 overflow-auto"
            >
              {searchResults.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPlayerId(p.id);
                      setSelectedPlayerLabel(`${p.name} (${p.position})`);
                      setSearchQ("");
                      setSearchResults([]);
                    }}
                    data-testid={`lobby-keeper-pick-${p.id}`}
                    className="w-full text-left px-3 py-2 font-body text-sm text-[#F5F5F5] hover:bg-[#252525]"
                  >
                    {p.name}{" "}
                    <span className="text-[#A0A0A0] text-xs">
                      {p.position} · {p.nfl_team || "-"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Value + add button */}
        <div className="flex items-center gap-2">
          <span className="font-body text-xs text-[#A0A0A0] shrink-0">$</span>
          <input
            type="number"
            min="1"
            value={keeperValue}
            onChange={(e) => setKeeperValue(e.target.value)}
            data-testid="lobby-keeper-value-input"
            className="flex-1 bg-[#1C1C1C] border border-[#252525] rounded-lg px-3 py-2 font-mono text-sm text-[#F5F5F5] outline-none"
          />
          <button
            type="button"
            onClick={onAdd}
            disabled={busy || !selectedTeamId || !selectedPlayerId}
            data-testid="lobby-keeper-add-btn"
            className="inline-flex items-center gap-1 bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold text-xs uppercase px-3 py-2 rounded-lg transition-colors"
          >
            <Plus size={12} /> Add Keeper
          </button>
        </div>
      </div>

      {/* Existing keepers grouped by team */}
      <div className="space-y-2">
        {keepers.length === 0 && (
          <p data-testid="lobby-keeper-empty" className="font-body text-xs text-[#A0A0A0] italic">
            No keepers assigned yet.
          </p>
        )}
        {(managers || []).map((m) => {
          const ks = byTeam[m.id] || [];
          if (!ks.length) return null;
          const teamName =
            m.team_name || `${m.first_name || ""} ${m.last_name || ""}`.trim();
          return (
            <div key={m.id} data-testid={`lobby-keeper-team-${m.id}`}>
              <p className="font-display font-bold text-xs uppercase tracking-wider text-[#A0A0A0] mb-1">
                {teamName}
              </p>
              <ul className="space-y-1">
                {ks.map((k) => (
                  <li
                    key={k.id}
                    className="flex items-center justify-between bg-[#1C1C1C] border border-[#252525] rounded-lg px-3 py-1.5"
                  >
                    <span className="font-body text-sm text-[#F5F5F5]">
                      {k.player_name}{" "}
                      <span className="text-[#A0A0A0] text-xs">
                        {k.player_position} · ${k.keeper_value}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemove(k.id)}
                      data-testid={`lobby-keeper-remove-${k.id}`}
                      className="text-[#A0A0A0] hover:text-[#FF4444] transition-colors"
                      title="Remove keeper"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
